import { z } from 'zod';
import { TitleOwnerIncomeType } from '../types';

// CT-3015 step 1. Save declared income + income type for every owner in one
// idempotent call (save-and-continue). NO_INCOME / PENSIONER may declare 0.
// This is a PARTIAL update — it only writes the fields it carries and never
// clobbers a prior verification (unless the income type itself changes).
export const updateTitleSearchOwnerSchema = z
  .object({
    applicationId: z.string().min(1, 'applicationId is required'),
    // Not defaulted: an omitted value (undefined) must stay distinct from an
    // explicit `false` so the service only clears a prior dispute on explicit confirm.
    ownershipDisputed: z.boolean().optional(),
    owners: z
      .array(
        z.object({
          id: z.string().min(1, 'owner id is required'),
          declaredIncome: z
            .number()
            .finite('declaredIncome must be a finite number')
            .nonnegative('declaredIncome must be ≥ 0'),
          incomeType: z.nativeEnum(TitleOwnerIncomeType),
          // NO_INCOME has no separate verify step — the "I have no income"
          // checkbox is captured here per owner. Optional (only meaningful for a
          // NO_INCOME owner); recorded on incomeVerificationDetails.hasNoIncome.
          // Left undefined for every other income type.
          hasNoIncome: z.boolean().optional(),
          // CT-3092. PENSIONER owners can carry their persisted IDMATRIX
          // reference through this partial save. When supplied it is validated
          // against the server-side ACCEPT (same guard as submit). Any existing
          // verification for the current income type is preserved whether or not
          // the reference is echoed; FAILED is recorded only when the pensioner
          // has no verification on record (or the FE explicitly sends
          // isIncomeVerified: false). Only meaningful for PENSIONER owners.
          idMatrixReference: z.string().min(1).nullish(),
          // CT-3092. Lets the FE explicitly mark a pensioner unverified (e.g. no
          // IDMatrix available) by sending false. Only false is accepted — the
          // BE derives the positive verdict server-side, so submitting true is
          // rejected. Mirrors submitTitleSearchOwnerSchema.
          isIncomeVerified: z
            .literal(false)
            .nullish()
            .describe(
              'Optional. Only false is accepted (explicit unverified, e.g. no IDMatrix). The BE derives the true verdict; submitting true is rejected.',
            ),
        }),
      )
      .default([]),
  })
  .superRefine((data, ctx) => {
    if (!data.ownershipDisputed && data.owners.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'array',
        inclusive: true,
        message: 'At least one owner is required when ownership is not disputed',
        path: ['owners'],
      });
    }
  });
